import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { clamp, roundTo } from '../types/geometry.js';
import { judgeRange, plainResult } from './support.js';

/**
 * Aspect ratio check.
 *
 * Also emits the *required* frame as a rectangle, which the overlay draws on
 * top of the user's photo so the excess area that has to be cropped away is
 * visible rather than described.
 */
export const aspectRatioCheck: ValidationCheck = {
  id: 'aspect_ratio',
  title: 'Формат: соотношение сторон',
  description: 'Проверяет пропорции кадра (например, строгий квадрат для визы США).',
  kind: 'technical',
  requires: ['imageMeta'],

  run: (ctx): CheckResult => {
    const minRatio = numberParam(ctx.rule.params, 'min', 0.99);
    const maxRatio = numberParam(ctx.rule.params, 'max', 1.01);
    const { width, height } = ctx.image;
    const ratio = height === 0 ? 0 : width / height;

    const verdict = judgeRange({
      value: ratio,
      min: minRatio,
      max: maxRatio,
      warningMargin: 0.002,
    });

    const targetRatio = clamp((minRatio + maxRatio) / 2, minRatio, maxRatio);
    const sourceAspect = width / height;
    // Rectangle with the required aspect ratio inscribed in the image.
    const requiredFrame =
      sourceAspect >= targetRatio
        ? {
            x: roundTo((1 - targetRatio / sourceAspect) / 2, 4),
            y: 0,
            width: roundTo(targetRatio / sourceAspect, 4),
            height: 1,
          }
        : {
            x: 0,
            y: roundTo((1 - sourceAspect / targetRatio) / 2, 4),
            width: 1,
            height: roundTo(sourceAspect / targetRatio, 4),
          };

    /**
     * Требуемый формат берётся из профиля: физические миллиметры (35×45) и
     * производное от них соотношение сторон — разные вещи, и пиксельные границы
     * не выводятся из миллиметров автоматически.
     */
    const requiredFraction = ctx.profile.image.requiredAspectRatio;
    const requiredText = requiredFraction
      ? `${requiredFraction.width}:${requiredFraction.height} (${roundTo(requiredFraction.width / requiredFraction.height, 3)})`
      : `${minRatio.toFixed(2)}–${maxRatio.toFixed(2)}`;
    const measuredText = roundTo(ratio, 3).toFixed(3).replace('.', ',');
    const requirement =
      ctx.profile.image.aspectRatioNote ??
      `Требуется соотношение сторон ${requiredText} (ширина / высота).`;

    return plainResult({
      id: aspectRatioCheck.id,
      title: aspectRatioCheck.title,
      status: verdict.status,
      severity: ctx.rule.severity,
      value: roundTo(ratio, 3),
      min: minRatio,
      max: maxRatio,
      message:
        verdict.status === 'pass'
          ? `Формат соответствует требованиям: ${requiredText}.`
          : verdict.status === 'uncertain'
            ? `Соотношение сторон на границе допуска: ваше ${measuredText}, требуется ${requiredText}.`
            : `Соотношение сторон не соответствует: ваше ${measuredText}, требуется ${requiredText}.`,
      requirement: `Соотношение сторон: ${requirement}`,
      fixability: 'fixable',
      geometry: [
        { type: 'rect', coordinates: [0, 0, 1, 1] },
        {
          type: 'rect',
          coordinates: [requiredFrame.x, requiredFrame.y, requiredFrame.width, requiredFrame.height],
        },
      ],
      confidence: 1,
      details: {
        ratio: roundTo(ratio, 4),
        targetRatio: roundTo(targetRatio, 4),
        requiredFrame,
        measured: measuredText,
        required: requiredText,
        measuredPixels: { width, height },
        requiredFraction: requiredFraction ?? null,
        nominalPhotoSize: ctx.profile.image.nominalPhotoSize ?? null,
        displayValue: measuredText,
      },
    });
  },
};
